import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function BreastCancerAwareness() {
  const { t } = useTranslation('blog');

  return (
    <>
      <title>Breast Cancer Awareness: Early Detection Saves Lives | SheCan</title>
      <meta name="description" content="Understanding breast cancer, early detection methods, self-examination guides, and support resources. Knowledge is power in the fight against breast cancer." />
      
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-pink-500 to-rose-400 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/8de5ec2b6dc59a1e62e1942137f41fc3.jpg"
            alt="Women supporting breast cancer awareness"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-pink-600/80 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div className="max-w-4xl">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
                <span className="text-white font-medium text-sm tracking-wider uppercase">
                  Breast Cancer Awareness
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Early Detection Saves Lives
              </h1>
              <p className="text-xl text-pink-50 mb-8 max-w-2xl mx-auto">
                Understanding breast cancer, knowing the signs, and taking action can make all the difference. You have the power to protect yourself.
              </p>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 py-12">
          
          {/* Key Statistics */}
          <div className="bg-gradient-to-br from-pink-100 to-rose-50 rounded-2xl p-8 mb-12 border border-pink-200">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-pink-600 mb-2">1 in 8</div>
                <p className="text-gray-700">Women will be diagnosed with breast cancer in their lifetime</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-pink-600 mb-2">99%</div>
                <p className="text-gray-700">Survival rate when detected early (Stage 0 or 1)</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-pink-600 mb-2">40+</div>
                <p className="text-gray-700">Age when regular mammograms should begin</p>
              </div>
            </div>
          </div>

          {/* Understanding Breast Cancer */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-heart-pulse-line text-pink-500"></i>
              Understanding Breast Cancer
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              <p className="text-lg mb-6">
                Breast cancer occurs when cells in the breast begin to grow abnormally and form a tumor. While it primarily affects women, men can also develop breast cancer. The good news? When caught early, breast cancer is highly treatable.
              </p>
              
              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Types of Breast Cancer</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 border-l-4 border-pink-400 shadow-sm">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">Ductal Carcinoma In Situ (DCIS)</h4>
                  <p>Cancer cells are confined to the milk ducts. Non-invasive and highly treatable.</p>
                </div>
                <div className="bg-white rounded-xl p-6 border-l-4 border-pink-400 shadow-sm">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">Invasive Ductal Carcinoma (IDC)</h4>
                  <p>Most common type. Cancer starts in milk ducts and spreads to surrounding breast tissue.</p>
                </div>
                <div className="bg-white rounded-xl p-6 border-l-4 border-pink-400 shadow-sm">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">Invasive Lobular Carcinoma (ILC)</h4>
                  <p>Starts in the milk-producing lobules and can spread to other parts of the body.</p>
                </div>
                <div className="bg-white rounded-xl p-6 border-l-4 border-pink-400 shadow-sm">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">Triple-Negative Breast Cancer</h4>
                  <p>More aggressive type that doesn't respond to hormone therapy. Requires different treatment approach.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Warning Signs */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-alert-line text-red-500"></i>
              Warning Signs You Should Never Ignore
            </h2>
            <div className="bg-red-50 rounded-2xl p-8 border border-red-200 mb-6">
              <p className="text-red-800 font-medium mb-4 text-lg">
                <i className="ri-error-warning-line mr-2"></i>
                If you notice ANY of these changes, schedule an appointment with your doctor immediately:
              </p>
              <ul className="space-y-3 text-gray-800">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>New lump</strong> in the breast or underarm that doesn't go away</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>Thickening or swelling</strong> of part of the breast</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>Dimpling or puckering</strong> of breast skin (looks like orange peel)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>Nipple changes:</strong> turning inward, discharge (other than breast milk), or bleeding</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>Skin changes:</strong> redness, scaliness, or irritation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>Pain</strong> in any area of the breast that doesn't go away</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-red-500 mt-1 flex-shrink-0"></i>
                  <span><strong>Change in size or shape</strong> of the breast</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 italic">
              Important: Most breast lumps are NOT cancer, but it's always better to get checked. Don't let fear stop you from seeking care.
            </p>
          </section>

          {/* Self-Examination Guide */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-hand-heart-line text-pink-500"></i>
              How to Perform a Breast Self-Exam
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Monthly self-exams help you become familiar with how your breasts normally look and feel. Do them 3-5 days after your period ends, or on the same day each month if you don't have periods.
            </p>
            
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">In the Shower</h3>
                    <p className="text-gray-700 mb-3">
                      With soapy hands, use the pads of your three middle fingers to check your entire breast and armpit area. Use light, medium, and firm pressure in a circular motion.
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                      <li>Start at the nipple and move outward in larger circles</li>
                      <li>Feel for any lumps, thickening, or changes</li>
                      <li>Check both breasts and armpits</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">In Front of a Mirror</h3>
                    <p className="text-gray-700 mb-3">
                      Visually inspect your breasts with your arms at your sides, then raised overhead. Look for:
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                      <li>Changes in size, shape, or symmetry</li>
                      <li>Skin dimpling, puckering, or bulging</li>
                      <li>Nipple changes (position, inversion)</li>
                      <li>Redness, rash, or swelling</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Lying Down</h3>
                    <p className="text-gray-700 mb-3">
                      Place a pillow under your right shoulder and put your right arm behind your head. Use your left hand to feel your right breast.
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                      <li>Use circular motions covering the entire breast</li>
                      <li>Apply light, medium, and firm pressure</li>
                      <li>Gently squeeze the nipple to check for discharge</li>
                      <li>Repeat on the other side</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Screening Guidelines */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-calendar-check-line text-pink-500"></i>
              Screening Guidelines by Age
            </h2>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">In Your 20s and 30s</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Monthly self-exams</li>
                  <li>• Clinical breast exam by doctor every 1-3 years</li>
                  <li>• Discuss family history with your doctor</li>
                  <li>• Know your baseline (how your breasts normally feel)</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ages 40-44</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Option to start annual mammograms (discuss with doctor)</li>
                  <li>• Continue monthly self-exams</li>
                  <li>• Annual clinical breast exam</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ages 45-54</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• <strong>Annual mammograms recommended</strong></li>
                  <li>• Monthly self-exams</li>
                  <li>• Consider additional screening if dense breast tissue</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Age 55+</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Mammograms every 1-2 years (or continue yearly)</li>
                  <li>• Continue as long as you're in good health</li>
                  <li>• Keep up with monthly self-exams</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-6 border border-red-200 mt-4">
                <p className="text-red-800 font-medium">
                  <i className="ri-alert-line mr-2"></i>
                  <strong>Higher Risk? Start Earlier:</strong> If you have a family history or genetic mutations (BRCA1/BRCA2), talk to your doctor about starting screening in your 20s or 30s.
                </p>
              </div>
            </div>
          </section>

          {/* Risk Factors & Prevention */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-shield-check-line text-pink-500"></i>
              Risk Factors & Prevention
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl p-6 border border-pink-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-red-600">Risk Factors You Can't Control</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-pink-500 mt-1 flex-shrink-0"></i>
                    <span>Being a woman (though men can get it too)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-pink-500 mt-1 flex-shrink-0"></i>
                    <span>Getting older (risk increases with age)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-pink-500 mt-1 flex-shrink-0"></i>
                    <span>Family history or genetic mutations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-pink-500 mt-1 flex-shrink-0"></i>
                    <span>Early menstruation (before age 12)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-pink-500 mt-1 flex-shrink-0"></i>
                    <span>Late menopause (after age 55)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-pink-500 mt-1 flex-shrink-0"></i>
                    <span>Dense breast tissue</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-green-700">What You CAN Do to Lower Risk</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Maintain a healthy weight</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Exercise regularly (at least 150 min/week)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Limit alcohol (no more than 1 drink/day)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Don't smoke</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Breastfeed if possible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Eat a diet rich in fruits and vegetables</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span>Know your family history and share it with your doctor</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Survivor Stories */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-sparkling-line text-pink-500"></i>
              Survivor Stories: You Are Not Alone
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-pink-50 to-white rounded-xl p-8 border border-pink-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 bg-pink-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-2xl text-pink-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Teresa, 42</h3>
                    <p className="text-gray-600">Diagnosed at Stage 1, now cancer-free for 3 years</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">
                  "I found a small lump during my monthly self-exam. I almost ignored it because it was tiny and didn't hurt, but something told me to get it checked. That decision saved my life. The cancer was caught early, and after surgery and radiation, I'm now cancer-free. Please, don't skip your self-exams. It takes 5 minutes and could save your life."
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-white rounded-xl p-8 border border-pink-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 bg-pink-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-2xl text-pink-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Maria, 35</h3>
                    <p className="text-gray-600">Triple-negative breast cancer survivor, 2 years in remission</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">
                  "Being diagnosed with an aggressive form at 33 was terrifying. But I learned that I was stronger than I ever knew. Chemotherapy was brutal, but my community of survivors lifted me up. Today, I live every day with gratitude and purpose. To anyone going through this: you are a warrior, and you will get through it."
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-white rounded-xl p-8 border border-pink-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 bg-pink-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-2xl text-pink-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Jennifer, 51</h3>
                    <p className="text-gray-600">Caught via routine mammogram, 5 years cancer-free</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">
                  "I had no symptoms, no lumps I could feel, nothing. But my routine mammogram showed something suspicious. It turned out to be early-stage cancer that was completely treatable. I never would have known without that screening. Please don't skip your mammograms. They truly save lives."
                </p>
              </div>
            </div>
          </section>

          {/* Support Resources */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-heart-3-line text-pink-500"></i>
              How to Support Someone with Breast Cancer
            </h2>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border border-pink-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What to Say & Do:</h3>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-heart-fill text-pink-500 mt-1 flex-shrink-0"></i>
                  <span>"I'm here for you. What do you need right now?"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-fill text-pink-500 mt-1 flex-shrink-0"></i>
                  <span>Offer specific help: "Can I bring you dinner on Tuesday?" "I'd like to drive you to your appointment."</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-fill text-pink-500 mt-1 flex-shrink-0"></i>
                  <span>Listen without trying to "fix" things or share cancer stories about other people</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-fill text-pink-500 mt-1 flex-shrink-0"></i>
                  <span>Help with practical tasks: housework, childcare, meal prep, grocery shopping</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-fill text-pink-500 mt-1 flex-shrink-0"></i>
                  <span>Send cards, texts, or care packages to show you're thinking of them</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-heart-fill text-pink-500 mt-1 flex-shrink-0"></i>
                  <span>Respect their privacy and follow their lead on what they want to share</span>
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-4">What NOT to Say:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                  <span>"Everything happens for a reason" or "God has a plan"</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                  <span>"You're so strong/brave" (puts pressure on them to be positive)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                  <span>"At least it's not..." (minimizes their experience)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                  <span>Stories about people who died from cancer</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                  <span>"Let me know if you need anything" (too vague - make specific offers instead)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Emergency Resources */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-lifebuoy-line text-pink-500"></i>
              Support Organizations & Resources
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">American Cancer Society</h3>
                <p className="text-gray-700 mb-3">24/7 support, information, and resources</p>
                <p className="text-pink-600 font-medium">1-800-227-2345</p>
                <a href="https://www.cancer.org" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 underline text-sm">
                  www.cancer.org
                </a>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Susan G. Komen</h3>
                <p className="text-gray-700 mb-3">Breast cancer education and support</p>
                <p className="text-pink-600 font-medium">1-877-465-6636</p>
                <a href="https://www.komen.org" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 underline text-sm">
                  www.komen.org
                </a>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">National Cancer Institute</h3>
                <p className="text-gray-700 mb-3">Research, clinical trials, and information</p>
                <p className="text-pink-600 font-medium">1-800-422-6237</p>
                <a href="https://www.cancer.gov" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 underline text-sm">
                  www.cancer.gov
                </a>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Breastcancer.org</h3>
                <p className="text-gray-700 mb-3">Comprehensive breast cancer information</p>
                <a href="https://www.breastcancer.org" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 underline text-sm">
                  www.breastcancer.org
                </a>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-pink-500 to-rose-400 rounded-2xl p-8 text-center text-white mb-12">
            <h2 className="text-3xl font-bold mb-4">Take Action Today</h2>
            <p className="text-xl text-pink-50 mb-6 max-w-2xl mx-auto">
              Schedule your mammogram, do a self-exam, or talk to your doctor about your risk factors. Early detection saves lives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/health/topics/breast-health"
                className="inline-flex items-center justify-center gap-2 bg-white text-pink-600 px-8 py-4 rounded-full font-semibold hover:bg-pink-50 transition-colors"
              >
                <i className="ri-heart-pulse-line text-xl"></i>
                Learn More About Breast Health
              </Link>
              <Link
                to="/health"
                className="inline-flex items-center justify-center gap-2 bg-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-pink-700 transition-colors border-2 border-white"
              >
                <i className="ri-question-answer-line text-xl"></i>
                Ask a Health Question
              </Link>
            </div>
          </div>

          {/* Tags & Share */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                Breast Cancer
              </span>
              <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                Women's Health
              </span>
              <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                Early Detection
              </span>
              <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                Cancer Awareness
              </span>
              <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                Prevention
              </span>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-gray-600">
                <i className="ri-calendar-line mr-2"></i>
                Published: January 2025
              </div>
              <div className="flex gap-3">
                <button className="w-10 h-10 bg-pink-100 hover:bg-pink-200 text-pink-600 rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-facebook-fill text-lg"></i>
                </button>
                <button className="w-10 h-10 bg-pink-100 hover:bg-pink-200 text-pink-600 rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </button>
                <button className="w-10 h-10 bg-pink-100 hover:bg-pink-200 text-pink-600 rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-lg"></i>
                </button>
                <button className="w-10 h-10 bg-pink-100 hover:bg-pink-200 text-pink-600 rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-share-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/health/topics/breast-health" className="group">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-40 bg-gradient-to-br from-pink-200 to-rose-200 flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-5xl text-pink-600"></i>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 group-hover:text-pink-600 transition-colors">
                      Complete Guide to Breast Health
                    </h4>
                  </div>
                </div>
              </Link>

              <Link to="/health/topics/gynecological-health" className="group">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-40 bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                    <i className="ri-hearts-line text-5xl text-purple-600"></i>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 group-hover:text-pink-600 transition-colors">
                      Gynecological Health Essentials
                    </h4>
                  </div>
                </div>
              </Link>

              <Link to="/health" className="group">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-40 bg-gradient-to-br from-teal-200 to-cyan-200 flex items-center justify-center">
                    <i className="ri-question-answer-line text-5xl text-teal-600"></i>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 group-hover:text-pink-600 transition-colors">
                      Ask Our Health Community
                    </h4>
                  </div>
                </div>
              </Link>
            </div>
          </div>

        </article>
      </div>
    </>
  );
}